<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { locale } from "svelte-i18n";

  import gift1Cn from "../assets/images/cn/gift1.png";
  import gift2Cn from "../assets/images/cn/gift2.png";
  import gift3Cn from "../assets/images/cn/gift3.png";
  import gift4Cn from "../assets/images/cn/gift4.png";
  import gift5Cn from "../assets/images/cn/gift5.png";
  import gift1Tc from "../assets/images/tc/gift1.png";
  import gift2Tc from "../assets/images/tc/gift2.png";
  import gift3Tc from "../assets/images/tc/gift3.png";
  import gift4Tc from "../assets/images/tc/gift4.png";
  import gift5Tc from "../assets/images/tc/gift5.png";

  /** 简体用 cn 图，其余（繁体 / 英文 / 韩文 / 站外默认）用 tc 图 */
  let images = $derived(
    $locale === "zh-cn"
      ? [gift1Cn, gift2Cn, gift3Cn, gift4Cn, gift5Cn]
      : [gift1Tc, gift2Tc, gift3Tc, gift4Tc, gift5Tc],
  );
  /** 两套图数量一样，轮播的环形计算用常量，不跟着语言变 */
  const len = 5;

  /*
    每张图的位置由 current 直接推导，偏移范围 [MIN_OFF, MAX_OFF]，
    可见位置只有 -1 / 0 / 1，其余在屏幕外透明待命。
    这样任何时刻三个可见位置都有图，不会出现空位。
  */
  const HALF = Math.floor(len / 2);
  const MAX_OFF = HALF;
  const MIN_OFF = HALF - len + 1;

  const AUTO_INTERVAL = 3000;
  const ANIM_DURATION = 500;
  const SWIPE_THRESHOLD = 40;

  let current = $state(0);
  /** 最近一次切换方向：用于识别跨越环边界的那张图，跨界时不做过渡 */
  let direction = $state<"next" | "prev">("next");

  let startX = 0;
  let deltaX = 0;
  let isDragging = false;
  let isAnimating = false;
  let autoTimer: ReturnType<typeof setInterval> | null = null;
  let animTimer: ReturnType<typeof setTimeout> | null = null;

  /** 当前图相对中心位的偏移 */
  function offsetOf(index: number): number {
    const off = (index - current + len) % len;
    return off > HALF ? off - len : off;
  }

  function go(step: 1 | -1) {
    if (isAnimating) return;
    isAnimating = true;
    direction = step === 1 ? "next" : "prev";
    current = (current + step + len) % len;

    if (animTimer) clearTimeout(animTimer);
    animTimer = setTimeout(() => {
      isAnimating = false;
    }, ANIM_DURATION);
  }

  function startAuto() {
    stopAuto();
    autoTimer = setInterval(() => go(1), AUTO_INTERVAL);
  }

  function stopAuto() {
    if (autoTimer) {
      clearInterval(autoTimer);
      autoTimer = null;
    }
  }

  function handleTouchStart(e: TouchEvent) {
    isDragging = true;
    startX = e.touches[0].clientX;
    deltaX = 0;
    stopAuto();
  }

  function handleTouchMove(e: TouchEvent) {
    if (!isDragging) return;
    deltaX = e.touches[0].clientX - startX;
  }

  function handleTouchEnd() {
    if (!isDragging) return;
    isDragging = false;
    if (deltaX < -SWIPE_THRESHOLD) go(1);
    else if (deltaX > SWIPE_THRESHOLD) go(-1);
    deltaX = 0;
    startAuto();
  }

  function getStyle(index: number): string {
    const off = offsetOf(index);
    const scale = off === 0 ? 1 : 0.6;
    const zIndex = off === 0 ? 10 : 5;
    // 环边界那张图是瞬移，不能带过渡，否则会横穿屏幕
    const wraps = direction === "next" ? off === MAX_OFF : off === MIN_OFF;

    let translate: string;
    let opacity = 1;
    switch (off) {
      case 0:
        translate = "translateX(0)";
        break;
      case 1:
        translate = "translateX(calc(80% + 16px))";
        break;
      case -1:
        translate = "translateX(calc(-80% - 16px))";
        break;
      case 2:
        translate = "translateX(calc(160% + 32px))";
        opacity = 0;
        break;
      case -2:
        translate = "translateX(calc(-160% - 32px))";
        opacity = 0;
        break;
      default:
        translate = `translateX(calc(${off * 80}% + ${off * 16}px))`;
        opacity = 0;
        break;
    }

    const transition = wraps
      ? "none"
      : `transform ${ANIM_DURATION}ms ease, opacity ${ANIM_DURATION}ms ease`;

    return `transform: ${translate} scale(${scale}); z-index: ${zIndex}; opacity: ${opacity}; transition: ${transition};`;
  }

  onMount(startAuto);

  onDestroy(() => {
    stopAuto();
    if (animTimer) clearTimeout(animTimer);
  });
</script>

<div
  class="w-full overflow-hidden relative pt-[185px] pb-[20px] touch-pan-y"
  ontouchstart={handleTouchStart}
  ontouchmove={handleTouchMove}
  ontouchend={handleTouchEnd}
>
  <div class="relative w-[60%] mx-auto aspect-[3/4]">
    {#each images as image, i (`gift-${i}`)}
      <div
        class="absolute top-0 left-0 w-full h-full flex items-center justify-center origin-center will-change-transform"
        style={getStyle(i)}
      >
        <img
          src={image}
          alt="gift {i + 1}"
          class="w-full h-full object-contain rounded-[12px] pointer-events-none select-none"
          draggable="false"
        />
      </div>
    {/each}
  </div>
</div>
